import { type OptionItem } from "@common/declarations";
import { Common } from "@common/types";
import { isUndefined } from "lodash";
import moment from "moment";
import apiV1, { type CacheType } from "./api";
import { type I18n } from "./i18n";

export type GetGetOptions<T> = () => Promise<Common.OptionItem<T>[]>;

export type CurrencyOptionItem = Common.OptionItem<string> & {
  blockchainNetworkKey: string;
  isCrypto: boolean;
  isTransfer: boolean;
};

export type GetCurrenciesRequest = {
  blockchainNetworkKey?: string;
  eventId?: string;
  integratedAppKey?: string;
  isActive?: boolean;
  isCommission?: boolean;
  countryCode?: string;
  isTransfer?: boolean;
  isWise?: boolean;
  payoutIntegrationId?: string;
};

export const DAY_TO_DAY_OF_WEEK: Common.GenericMap<Common.DayOfWeek> = {
  1: "Sunday",
  2: "Monday",
  3: "Tuesday",
  4: "Wednesday",
  5: "Thursday",
  6: "Friday",
  7: "Saturday"
};

const getOptions = (i18n: I18n) => {
  const v1GetOptions = async <T extends Common.Primitive>(
    space: string,
    tableName: string,
    parameters?: object,
    cache?: CacheType | boolean,
    sort?: Common.SortOrder,
    isHost?: boolean
  ): Promise<Common.OptionItem<T>[]> => {
    let items = await apiV1.getDropdownItems<T>(i18n, space, tableName, parameters, cache, isHost);
    if (sort) items = items.sort((a, b) => a.label.localeCompare(b.label) * (sort === "asc" ? 1 : -1));
    return items;
  };

  const getCustomOptions = async <TOptionItem extends OptionItem<any>>(
    space: string,
    tableName: string,
    parameters?: object,
    cache?: CacheType | boolean
  ) => {
    let items = await v1GetOptions(space, tableName, parameters, cache, undefined, false);
    return items as TOptionItem[];
  };

  const getCustomOptionsHost = async <TOptionItem extends OptionItem<any>>(
    space: string,
    tableName: string,
    parameters?: object,
    cache?: CacheType | boolean
  ) => {
    // TODO: if on host, use local URL
    let items = await v1GetOptions(space, tableName, parameters, cache, undefined, true);
    return items as TOptionItem[];
  };

  const timeUnits = async (plural: boolean, sort?: Common.SortOrder, params?: object) => {
    let options = await v1GetOptions<string>("system", "timeUnits", params, "memory");
    options = options.sort(
      (a, b) =>
        (moment()
          .add(1, a.value as moment.unitOfTime.DurationAs)
          .valueOf() -
          moment()
            .add(1, b.value as moment.unitOfTime.DurationAs)
            .valueOf()) *
        ((sort ?? "asc") === "asc" ? 1 : -1)
    );
    if (plural) {
      return options.map((x) => ({ ...x, label: `${x.label}s` }));
    }
    return options;
  };

  return {
    yesNo: (yesLabel?: string, noLabel?: string): Common.OptionItem<boolean>[] => [
      { label: yesLabel ?? i18n.yes, value: true },
      { label: noLabel ?? i18n.no, value: false }
    ],
    bottomTop: (): Common.OptionItem<boolean>[] => [
      { label: i18n.bottom, value: true },
      { label: i18n.top, value: false }
    ],
    numberRange: (
      from: number,
      to: number,
      increment: number = 1,
      labelSymbol: string = ""
    ): Common.OptionItem<number>[] =>
      Array.from({ length: to - from + 1 }, (_, index) => ({
        label: ((from + index) * increment).toString() + labelSymbol,
        value: (from + index) * increment
      })),
    percentRange: (from: number, to: number, increment: number = 1): Common.OptionItem<number>[] =>
      Array.from({ length: to - from + 1 }, (_, index) => ({
        label: ((from + index) * increment).toString() + "%",
        value: ((from + index) * increment) / 100
      })),
    months: (format: string): Common.OptionItem<number>[] =>
      Array.from({ length: 12 }, (_, index) => ({
        label: moment().month(index).format(format),
        value: 1 + index
      })),
    daysOfWeek: (): Common.OptionItem<number>[] =>
      Object.keys(DAY_TO_DAY_OF_WEEK).map((x) => ({
        label: i18n[DAY_TO_DAY_OF_WEEK[x]],
        value: +x
      })),
    enabledDisabled: (): Common.OptionItem<boolean>[] => [
      { label: i18n.disabled, value: false },
      { label: i18n.enabled, value: true }
    ],
    publishedUnpublished: (): Common.OptionItem<boolean>[] => [
      { label: i18n.published, value: true },
      { label: i18n.unpublished, value: false }
    ],
    privatePublic: (): Common.OptionItem<boolean>[] => [
      { label: i18n.private, value: true },
      { label: i18n.public, value: false }
    ],
    publicPrivate: (): Common.OptionItem<boolean>[] => [
      { label: i18n.public, value: true },
      { label: i18n.private, value: false }
    ],
    timePeriodLength: (): Common.OptionItem<string>[] => [
      { label: "Year", value: "Year" },
      { label: "Month", value: "Month" },
      { label: "Week", value: "Week" },
      { label: "Day", value: "Day" },
      { label: "Hour", value: "Hour" },
      { label: "Minute", value: "Minute" },
      { label: "Second", value: "Second" }
    ],
    countries: () => v1GetOptions<string>("system", "countries", { v: 1 }, "memory", "asc"),
    cultures: (isEnabled?: boolean) => v1GetOptions<string>("system", "cultures", { v: 1, isEnabled }, "memory", "asc"),
    currencies: async (data?: GetCurrenciesRequest, cache?: CacheType) =>
      getCustomOptions<CurrencyOptionItem>("system", "currencies", { v: 2, ...data }, cache ?? "memory"),
    currenciesHost: async (data?: GetCurrenciesRequest, cache?: CacheType) =>
      getCustomOptionsHost<CurrencyOptionItem>("system", "currencies", { v: 1, ...data }, cache ?? "memory"),
    wiseCurrencies: () => getCustomOptions<CurrencyOptionItem>("system", "currencies", { isWise: true }, "memory"),
    wiseCurrenciesHost: () =>
      getCustomOptionsHost<CurrencyOptionItem>("system", "currencies", { isWise: true }, "memory"),
    portal: {
      events: (isDomainless: boolean = false) =>
        v1GetOptions<string>(
          "web",
          "portals",
          {
            includeEvent: true,
            includeProgram: false,
            includeWebsite: false,
            includeDomainless: isDomainless,
            isDeleted: false
          },
          undefined,
          "asc"
        ),
      programs: (isDomainless: boolean = false) =>
        v1GetOptions<string>(
          "web",
          "portals",
          {
            includeEvent: false,
            includeProgram: true,
            includeWebsite: false,
            includeDomainless: isDomainless,
            isDeleted: false
          },
          undefined,
          "asc"
        ),
      websites: (isDomainless: boolean = false) =>
        v1GetOptions<string>(
          "web",
          "portals",
          {
            includeEvent: false,
            includeProgram: false,
            includeWebsite: true,
            includeDomainless: isDomainless,
            isDeleted: false
          },
          undefined,
          "asc"
        ),
      domainless: () =>
        v1GetOptions<string>(
          "web",
          "portals",
          {
            includeEvent: false,
            includeProgram: false,
            includeWebsite: false,
            includeDomainless: true,
            isDeleted: false
          },
          undefined,
          "asc"
        )
    },
    /**
     * @param countryCode null - nothing, undefined - all regions
     * @returns
     */
    regions: (countryCode?: string | null) =>
      countryCode || isUndefined(countryCode)
        ? v1GetOptions<string>("system", "subdivisions", { countryCode, v: 1 }, "memory")
        : Promise.resolve([]),
    allRegions: () => v1GetOptions<string>("system", "subdivisions", { isAll: true }, "memory"),
    periods: async (sort?: Common.SortOrder) => {
      const tus = await timeUnits(false, sort);
      return tus
        .filter((x) => ["day", "week", "month", "year"].includes(x.value.toLowerCase()))
        .map((x) => ({
          ...x,
          label: x.value === "Day" ? i18n.day : i18n[`${x.value.toLowerCase()}`]
        }));
    },
    timeUnits,
    timezones: async (nullLabel?: string) => {
      let options: Common.OptionItem<string | null>[] = await v1GetOptions<string>(
        "system",
        "timeZones",
        undefined,
        "memory"
      );
      options = options.sort((a, b) => a.label.localeCompare(b.label));
      if (nullLabel) {
        options = [{ label: nullLabel, value: null }, ...options];
      }
      return options;
    },
    getOptions: v1GetOptions,
    getOptionsPage: async <T extends Common.Api.PageRequest, R extends string | number = string | number>(
      space: string,
      tableName: string,
      parameters: T,
      cache?: RequestCache
    ) => {
      const options = await apiV1.getDropdownItems<R>(i18n, space, tableName, parameters, cache);
      return {
        items: options.slice(0, parameters.size),
        hasMore: options.length == parameters.size
      };
    },
    getCustomOptions,
    getCustomOptionsHost,
    getCustomOptionsPage: async <R extends OptionItem<any>>(
      space: string,
      tableName: string,
      parameters: any,
      cache?: RequestCache
    ) => {
      const res = await getCustomOptions(space, tableName, parameters, cache);
      return {
        items: res.slice(0, parameters.size) as R[],
        hasMore: res.length == parameters.size
      };
    },
    getCustomOptionsPageHost: async <R extends OptionItem<any>>(
      space: string,
      tableName: string,
      parameters: any,
      cache?: RequestCache
    ) => {
      // TODO: if on host, use local URL
      const res = await getCustomOptionsHost(space, tableName, parameters, cache);
      return {
        items: res.slice(0, parameters.size) as R[],
        hasMore: res.length == parameters.size
      };
    },
    // move parameters, sort and isHost into an object (opts)
    getGetOptions:
      <T extends Common.Primitive>(space: string, tableName: string, parameters?: object, sort?: Common.SortOrder) =>
      (): Promise<Common.OptionItem<T>[]> =>
        v1GetOptions(space, tableName, parameters, undefined, sort)
  };
};
export default getOptions;

export const doesOptionMatchText = <T extends OptionItem<any>>(
  node: T,
  searchText: string | undefined | null
): boolean =>
  !searchText ||
  node.label?.toLowerCase().includes(searchText.toLowerCase()) ||
  !!node.descriptions?.some((description) => description.label.toLowerCase().includes(searchText.toLowerCase())) ||
  !!node.group?.toLowerCase().includes(searchText.toLowerCase());
